import React from 'react';

import { MEDIA_TYPE } from 'constants/MediaConstants';
import type { MayBe } from 'modules/global/typing/common/common';
import type { AbstractMedia } from 'modules/medias/types/abstract-media';

type UseVideoDurationParams = Pick<AbstractMedia, 'path' | 'type'> & {
  time?: number;
};

type UseVideoDuration = (params: UseVideoDurationParams) => number;

const useVideoDuration: UseVideoDuration = ({ path: mediaPath, type, time: defaultDuration = 0 }) => {
  const [duration, setDuration] = React.useState(defaultDuration);
  const videoRef = React.useRef<MayBe<HTMLVideoElement>>(undefined);

  const handleDurationChange = React.useCallback((event: Event) => {
    const target = event.target as HTMLVideoElement;
    if (target.duration !== Infinity) {
      setDuration(target.duration);
    }
  }, []);

  const handleError = React.useCallback((event: Event) => {
    // The source is empty, expired or unsupported: swallow the load failure so it
    // does not escape as an unhandled promise rejection (DOMException NotSupportedError).
    // The duration simply stays at its default value.
    const target = event.target as HTMLVideoElement;
    target.removeAttribute('src');
  }, []);

  React.useEffect(() => {
    if (type === MEDIA_TYPE.VIDEO && !duration && mediaPath) {
      videoRef.current ??= document.createElement('video');
      const video = videoRef.current;

      video.addEventListener('durationchange', handleDurationChange);
      video.addEventListener('error', handleError);
      video.src = mediaPath;
      // Fake timer, to force the video to load and trigger the durationchange event
      video.currentTime = 86400;
    }

    return () => {
      videoRef.current?.removeEventListener('durationchange', handleDurationChange);
      videoRef.current?.removeEventListener('error', handleError);
    };
  }, [duration, handleDurationChange, handleError, mediaPath, type]);

  return duration;
};

export default useVideoDuration;
